import { fetchJSON as fetchAPI } from './client';

export type ScreenInventoryEntry = {
  key: string;
  name: string;
  category: string;
  route?: string;
  priority: string;
  mvp: boolean;
};

export type ScreenSpecification = {
  key: string;
  name: string;
  purpose: string;
  layout: string;
  components: string[];
  interactions: string[];
  keyboard_shortcuts?: string[];
  loading_behaviour: string;
  empty_state: string;
  error_state: string;
  permissions?: string[];
  performance_target: string;
  ai_behaviour?: string;
  navigation_rules?: string[];
  success_criteria: string[];
};

export type LayoutRegion = {
  key: string;
  name: string;
  grid_area?: string;
  order: number;
  component?: string;
  description?: string;
};

export type WorkspaceLayout = {
  key: string;
  name: string;
  layout_type: string;
  tabs?: string[];
  regions: LayoutRegion[];
  actions?: string[];
};

export type ComponentPlacement = {
  key: string;
  component: string;
  screen: string;
  region: string;
  position: string;
  size?: string;
  priority: number;
};

export type ScreenBehaviour = {
  key: string;
  name: string;
  category: string;
  description: string;
  target?: string;
};

export type KeyboardShortcut = {
  key: string;
  shortcut: string;
  action: string;
  scope: string;
  description?: string;
};

export type ResponsiveRule = {
  key: string;
  breakpoint: string;
  layout: string;
  hidden?: string[];
  collapsed?: string[];
  description?: string;
};

export type AccessibilitySpec = {
  key: string;
  name: string;
  requirement: string;
  mandatory: boolean;
};

export type ScreenDeliverable = {
  key: string;
  name: string;
  format?: string;
  status: string;
  description?: string;
};

export type UXSDashboard = {
  bible_id: string;
  tenant_id: string;
  version: string;
  depends_on: string[];
  screen_inventory: number;
  screen_specifications: number;
  workspace_layouts: number;
  component_placements: number;
  screen_behaviours: number;
  keyboard_shortcuts: number;
  responsive_rules: number;
  accessibility_specs: number;
  deliverables: number;
  generated_at: string;
};

export type ScreenSpecValidation = {
  screen_key: string;
  valid: boolean;
  purpose_defined: boolean;
  layout_specified: boolean;
  components_placed: boolean;
  interactions_documented: boolean;
  keyboard_shortcuts: boolean;
  loading_behaviour: boolean;
  empty_state: boolean;
  error_state: boolean;
  permissions: boolean;
  performance_target: boolean;
  ai_behaviour: boolean;
  navigation_rules: boolean;
  success_criteria: boolean;
  gaps?: string[];
  evaluated_at: string;
};

export type UXSBibleStatus = {
  bible_id: string;
  version: string;
  depends_on: string[];
  screen_inventory: number;
  screen_specifications: number;
  workspace_layouts: number;
  component_placements: number;
  screen_behaviours: number;
  keyboard_shortcuts: number;
  responsive_rules: number;
  accessibility_specs: number;
  deliverables: number;
  fully_specified: boolean;
  no_ux_decisions_needed: boolean;
  status: string;
};

const MARKETINTEL_BASE = import.meta.env.VITE_MARKETINTEL_API ?? '/api/v1/marketintel';

function fetchMarketIntel<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${MARKETINTEL_BASE}${path}`, init);
}

export const marketplaceScreenBibleApi = {
  dashboard: () => fetchMarketIntel<UXSDashboard>('/screen-bible/dashboard'),
  screenInventory: () => fetchMarketIntel<{ screens: ScreenInventoryEntry[] }>('/screen-bible/screen-inventory').then((r) => r.screens),
  screenSpecifications: () => fetchMarketIntel<{ specifications: ScreenSpecification[] }>('/screen-bible/screen-specifications').then((r) => r.specifications),
  dashboardLayout: () => fetchMarketIntel<WorkspaceLayout>('/screen-bible/dashboard-layout'),
  workspaceLayouts: () => fetchMarketIntel<{ layouts: WorkspaceLayout[] }>('/screen-bible/workspace-layouts').then((r) => r.layouts),
  componentPlacements: () => fetchMarketIntel<{ placements: ComponentPlacement[] }>('/screen-bible/component-placements').then((r) => r.placements),
  screenBehaviours: () => fetchMarketIntel<{ behaviours: ScreenBehaviour[] }>('/screen-bible/screen-behaviours').then((r) => r.behaviours),
  keyboardShortcuts: () => fetchMarketIntel<{ shortcuts: KeyboardShortcut[] }>('/screen-bible/keyboard-shortcuts').then((r) => r.shortcuts),
  responsiveRules: () => fetchMarketIntel<{ rules: ResponsiveRule[] }>('/screen-bible/responsive-rules').then((r) => r.rules),
  accessibility: () => fetchMarketIntel<{ specs: AccessibilitySpec[] }>('/screen-bible/accessibility').then((r) => r.specs),
  deliverables: () => fetchMarketIntel<{ deliverables: ScreenDeliverable[] }>('/screen-bible/deliverables').then((r) => r.deliverables),
  validateScreen: (payload: { screen_key: string; attrs: Record<string, boolean> }) =>
    fetchMarketIntel<ScreenSpecValidation>('/screen-bible/validate-screen', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  status: () => fetchMarketIntel<UXSBibleStatus>('/screen-bible/status'),
};
